import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { apiClient } from '@/services/api'
import {
  ArrowLeft, Users, TrendingUp, Clock, Target, BarChart2,
  Download, FileSpreadsheet, FileText, Loader2, ExternalLink, Award, Activity,
} from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { useTerminology } from '@/hooks/useTerminology'
import type { CourseRunsAnalytics, CourseRunMetrics, CourseOverview, CourseRunOverview } from '@/types'
import {
  generateCourseExcelReport,
  generateCoursePdfReport,
  getCurrentPdfTheme,
  type CourseReportData,
  type LessonReportData,
  type RawResponse,
} from '@/utils/reportGenerator'

type TabKey = 'summary' | 'runs' | 'export'

export function CourseReport() {
  const { organizationId, courseId } = useParams<{ organizationId: string; courseId: string }>()
  const navigate = useNavigate()
  const t = useTerminology()

  const [data, setData] = useState<CourseRunsAnalytics | null>(null)
  const [courseName, setCourseName] = useState('')
  const [courseOverview, setCourseOverview] = useState<CourseOverview | null>(null)
  const [runOverviews, setRunOverviews] = useState<Array<{ runId: string; runTitle: string; overview: CourseRunOverview }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'summary')

  useEffect(() => {
    if (!courseId) return
    const load = async () => {
      try {
        setLoading(true)
        const [runsRes, overviewRes] = await Promise.allSettled([
          apiClient.getCourseRunsAnalytics(courseId),
          apiClient.getCourseOverview(courseId),
        ])
        let runsData: CourseRunsAnalytics | null = null
        if (runsRes.status === 'fulfilled') {
          const d = runsRes.value.data as any
          runsData = d?.runs ? d : runsRes.value.data
          setData(runsData)
        }
        if (overviewRes.status === 'fulfilled') {
          const o = overviewRes.value.data as any
          setCourseName(o?.course?.name ?? '')
          setCourseOverview(o)
        }
        // Fetch per-run lesson breakdowns for cross-run comparison
        if (runsData?.runs?.length) {
          const runResults = await Promise.allSettled(
            runsData.runs.map(run => apiClient.getCourseRunOverview(courseId, run.id))
          )
          const overviews: Array<{ runId: string; runTitle: string; overview: CourseRunOverview }> = []
          runResults.forEach((result, idx) => {
            if (result.status === 'fulfilled') {
              const run = runsData!.runs[idx]
              overviews.push({
                runId: run.id,
                runTitle: run.title || `Run ${idx + 1}`,
                overview: result.value.data as CourseRunOverview,
              })
            }
          })
          setRunOverviews(overviews)
        }
      } catch (err: any) {
        const apiError = err?.response?.data?.error
        setError(typeof apiError === 'string' ? apiError : apiError?.message || err?.message || 'Failed to load course report')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [courseId])

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-theme-text-muted font-mono">LOADING REPORT</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error ?? 'Report not available'}</p>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-surface-hover rounded-lg text-theme-text hover:bg-surface-hover transition-colors">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const { runs, aggregated, trends } = data

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Header */}
      <div className="border-b border-theme-border bg-chrome-bg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-surface-hover text-theme-text hover:bg-surface-hover transition-colors shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-green-500">{courseName || 'Course'} — Report</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-tour="course-report-summary">
          <StatCard icon={TrendingUp} label="Total Runs" value={aggregated.totalRuns} color="green" />
          <StatCard icon={Users} label="Avg Students/Run" value={aggregated.avgStudentsPerRun} color="blue" />
          <StatCard icon={Clock} label="Avg Duration" value={`${aggregated.avgDuration}m`} color="yellow" />
          <StatCard icon={Target} label="Avg Response Rate" value={`${aggregated.avgResponseRate}%`} color="purple" />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-theme-border" data-tour="course-report-runs">
          {([
            { key: 'summary', label: 'Summary' },
            { key: 'runs', label: 'Runs' },
            { key: 'export', label: 'Export' },
          ] as { key: TabKey; label: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 font-medium border-b-2 transition-colors text-sm ${
                activeTab === tab.key
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-theme-text-muted hover:text-theme-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'summary' && (
          <SummaryTab runs={runs} aggregated={aggregated} trends={trends} courseOverview={courseOverview} runOverviews={runOverviews} />
        )}
        {activeTab === 'runs' && (
          <RunsTab runs={runs} organizationId={organizationId!} courseId={courseId!} navigate={navigate} />
        )}
        {activeTab === 'export' && (
          <ExportTab courseId={courseId!} courseName={courseName} />
        )}
      </div>
    </div>
  )
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    green: 'bg-green-900/30 text-green-400',
    blue: 'bg-blue-900/30 text-blue-400',
    yellow: 'bg-yellow-900/30 text-yellow-400',
    purple: 'bg-purple-900/30 text-purple-400',
    cyan: 'bg-cyan-900/30 text-cyan-400',
  }
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-surface-deep rounded-xl p-4 border border-theme-border">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${colorMap[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-theme-text-muted mt-0.5">{label}</div>
    </motion.div>
  )
}

function SummaryTab({ runs, aggregated, trends, courseOverview, runOverviews }: { runs: CourseRunMetrics[]; aggregated: CourseRunsAnalytics['aggregated']; trends: CourseRunsAnalytics['trends']; courseOverview: CourseOverview | null; runOverviews: Array<{ runId: string; runTitle: string; overview: CourseRunOverview }> }) {
  const chartData = trends.labels.map((label, idx) => ({
    date: label,
    students: trends.studentCounts[idx],
    responseRate: trends.responseRates[idx],
    avgScore: trends.avgScores[idx],
  }))

  const tooltipStyle = { background: 'var(--color-surface-deep)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }

  const [perLessonMetric, setPerLessonMetric] = useState<'avgScore' | 'responseRate'>('avgScore')

  // Build per-lesson cross-run comparison data
  const runColors = ['#4ADE80', '#60A5FA', '#C084FC', '#FACC15', '#F87171', '#2DD4BF', '#FB923C', '#A78BFA']

  const perLessonCrossRunData = React.useMemo(() => {
    if (runOverviews.length === 0) return []

    // Collect all unique lesson names in order
    const lessonNamesSet = new Map<string, string>() // lessonName -> lessonId
    for (const ro of runOverviews) {
      for (const lb of ro.overview.lessonBreakdown) {
        if (!lessonNamesSet.has(lb.lessonName)) {
          lessonNamesSet.set(lb.lessonName, lb.lessonId)
        }
      }
    }

    // Build chart data: one entry per lesson, with each run's metric as a separate key
    const data: Array<Record<string, any>> = []
    for (const [lessonName] of lessonNamesSet) {
      const entry: Record<string, any> = {
        name: lessonName.length > 25 ? lessonName.slice(0, 25) + '...' : lessonName,
        fullName: lessonName,
      }
      for (const ro of runOverviews) {
        const lessonData = ro.overview.lessonBreakdown.find(lb => lb.lessonName === lessonName)
        entry[`${ro.runTitle}_avgScore`] = lessonData?.avgScore ?? null
        entry[`${ro.runTitle}_responseRate`] = lessonData?.responseRate ?? null
      }
      data.push(entry)
    }
    return data
  }, [runOverviews])

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Course-level Metric Cards */}
      {courseOverview?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Total Students" value={courseOverview.summary.totalStudents} color="blue" />
          <StatCard icon={Target} label="Response Rate" value={`${courseOverview.summary.responseRate}%`} color="green" />
          <StatCard icon={Award} label="Avg Score" value={Math.round(courseOverview.summary.avgScore)} color="yellow" />
          <StatCard icon={Activity} label="Completion Rate" value={`${courseOverview.summary.completionRate}%`} color="purple" />
        </div>
      )}

      {/* Per-Lesson Performance Across Runs */}
      {perLessonCrossRunData.length > 0 && runOverviews.length > 0 && (
        <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide">Per-Lesson Performance Across Runs</h3>
            <div className="flex gap-1 bg-chrome-bg rounded-lg p-0.5">
              <button
                onClick={() => setPerLessonMetric('avgScore')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${perLessonMetric === 'avgScore' ? 'bg-surface-hover text-theme-text font-medium' : 'text-theme-text-muted hover:text-theme-text'}`}
              >
                Avg Score
              </button>
              <button
                onClick={() => setPerLessonMetric('responseRate')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${perLessonMetric === 'responseRate' ? 'bg-surface-hover text-theme-text font-medium' : 'text-theme-text-muted hover:text-theme-text'}`}
              >
                Response Rate
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(300, perLessonCrossRunData.length * 20 + 100)}>
            <BarChart data={perLessonCrossRunData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" angle={-30} textAnchor="end" height={80} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} domain={[0, 100]} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: any, name: string) => {
                  const label = name.replace(`_${perLessonMetric}`, '')
                  return [value != null ? `${value}%` : 'N/A', label]
                }}
                labelFormatter={(label: string, payload: any[]) => payload?.[0]?.payload?.fullName || label}
              />
              <Legend formatter={(value: string) => value.replace(`_${perLessonMetric}`, '')} />
              {runOverviews.map((ro, idx) => (
                <Bar
                  key={ro.runId}
                  dataKey={`${ro.runTitle}_${perLessonMetric}`}
                  name={`${ro.runTitle}_${perLessonMetric}`}
                  fill={runColors[idx % runColors.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Run Trends Line Chart */}
      {chartData.length > 1 && (
        <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
          <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide mb-4">Run Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Line type="monotone" dataKey="students" name="Students" stroke="#4ADE80" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="responseRate" name="Response Rate %" stroke="#60A5FA" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="avgScore" name="Avg Score" stroke="#C084FC" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Run Comparison Bar Chart */}
      {runs.length > 1 && (
        <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
          <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide mb-4">Run Comparison</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={runs.map((run, idx) => ({
                name: run.title || (run.startedAt ? new Date(run.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Run ${idx + 1}`),
                responseRate: run.lessonMetrics?.responseRate ?? 0,
                avgScore: run.lessonMetrics?.avgScore ?? 0,
                students: run.studentCount,
              }))}
              margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Bar dataKey="responseRate" name="Response Rate %" fill="#4ADE80" radius={[4, 4, 0, 0]} />
              <Bar dataKey="avgScore" name="Avg Score" fill="#60A5FA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Students */}
      {courseOverview?.studentLeaderboard && courseOverview.studentLeaderboard.length > 0 && (
        <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
          <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide mb-4">Top Students</h3>
          <div className="space-y-2">
            {courseOverview.studentLeaderboard.slice(0, 10).map((student, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-chrome-bg transition-colors">
                <div className="w-7 h-7 rounded-full bg-surface-hover flex items-center justify-center text-sm font-bold text-theme-text-muted shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0 font-medium truncate">{student.displayName}</div>
                <div className="flex items-center gap-4 text-sm shrink-0">
                  <span className="text-yellow-400 font-semibold">{Math.round(student.avgScore)} avg</span>
                  <span className="text-theme-text-muted">{student.responseRate}% responses</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}

function RunsTab({ runs, organizationId, courseId, navigate }: { runs: CourseRunMetrics[]; organizationId: string; courseId: string; navigate: any }) {
  const t = useTerminology()
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      {runs.length === 0 ? (
        <div className="text-center py-16 text-theme-muted">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No completed runs yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-theme-text-muted border-b border-theme-border">
                <th className="pb-2 pr-4">#</th>
                <th className="pb-2 pr-4">Run</th>
                <th className="pb-2 pr-4">Date</th>
                <th className="pb-2 pr-4 text-right">{t.lesson.titlePlural}</th>
                <th className="pb-2 pr-4 text-right">Students</th>
                <th className="pb-2 pr-4 text-right">Duration</th>
                <th className="pb-2 pr-4 text-right">Response Rate</th>
                <th className="pb-2 pr-4 text-right">Avg Score</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run, idx) => (
                <tr key={run.id} className="border-b border-theme-border hover:bg-chrome-bg transition-colors">
                  <td className="py-3 pr-4 text-theme-muted font-mono">{idx + 1}</td>
                  <td className="py-3 pr-4 font-medium">{run.title || `Run ${idx + 1}`}</td>
                  <td className="py-3 pr-4 text-theme-text-muted font-mono text-xs">
                    {run.startedAt ? new Date(run.startedAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="py-3 pr-4 text-right">{run.lessonCount}</td>
                  <td className="py-3 pr-4 text-right">{run.studentCount}</td>
                  <td className="py-3 pr-4 text-right text-theme-text-muted">{run.duration}m</td>
                  <td className="py-3 pr-4 text-right">{run.lessonMetrics?.responseRate ?? '-'}%</td>
                  <td className="py-3 pr-4 text-right">{run.lessonMetrics?.avgScore ?? '-'}</td>
                  <td className="py-3">
                    <button
                      onClick={() => {
                        const url = `/organizations/${organizationId}/courses/${courseId}/runs/${run.id}/report`
                        navigate(url)
                      }}
                      className="p-1.5 bg-surface-hover text-theme-text rounded-lg hover:bg-surface-hover transition-colors"
                      title="View run report"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  )
}

function ExportTab({ courseId, courseName }: { courseId: string; courseName: string }) {
  const t = useTerminology()
  const [excelLoading, setExcelLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [progress, setProgress] = useState('')

  const fetchReportData = async (): Promise<CourseReportData> => {
    const courseRes = await apiClient.getCourseOverview(courseId)
    const overview = (courseRes.data as any)

    const course = { name: overview.course.name, course_code: overview.course.courseCode, status: overview.course.status } as any

    const lessonData: LessonReportData[] = []
    const breakdown = overview.lessonBreakdown || []

    for (let i = 0; i < breakdown.length; i++) {
      const lb = breakdown[i]
      setProgress(`Loading lesson ${i + 1}/${breakdown.length}...`)

      const [lessonRes, analyticsRes] = await Promise.allSettled([
        apiClient.getLesson(lb.lessonId),
        apiClient.getLessonPlayersAnalytics(lb.lessonId),
      ])

      const fullLesson = lessonRes.status === 'fulfilled'
        ? ((lessonRes.value.data as any)?.lesson ?? lessonRes.value.data)
        : { id: lb.lessonId, name: lb.lessonName, activities: [], players: [] }
      const activities = fullLesson.activities ?? []
      const players = fullLesson.players ?? []

      const rawAnalytics: any[] = analyticsRes.status === 'fulfilled'
        ? ((analyticsRes.value.data as any)?.players ?? analyticsRes.value.data ?? [])
        : []
      const playerAnalytics: Record<string, any> = {}
      for (const a of rawAnalytics) playerAnalytics[a.id] = a

      const responseResults = await Promise.allSettled(
        activities.map((act: any) => apiClient.getResponses(fullLesson.id, act.id))
      )
      const activityResponses: Record<string, RawResponse[]> = {}
      activities.forEach((act: any, idx: number) => {
        const result = responseResults[idx]
        activityResponses[act.id] = result.status === 'fulfilled'
          ? ((result.value.data as any)?.responses ?? result.value.data ?? [])
          : []
      })

      lessonData.push({ lesson: fullLesson, activities, activityResponses, players, playerAnalytics })
    }

    setProgress('')
    return { course, lessons: lessonData }
  }

  const handleExcel = async () => {
    setExcelLoading(true)
    try {
      const data = await fetchReportData()
      await generateCourseExcelReport(data)
    } catch (e) { }
    finally { setExcelLoading(false); setProgress('') }
  }

  const handlePdf = async () => {
    setPdfLoading(true)
    try {
      const data = await fetchReportData()
      await generateCoursePdfReport(data, getCurrentPdfTheme())
    } catch (e) { }
    finally { setPdfLoading(false); setProgress('') }
  }

  const isLoading = excelLoading || pdfLoading

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      {isLoading && progress && (
        <div className="flex items-center gap-3 p-4 bg-blue-900/30 border border-blue-800 rounded-xl text-blue-300 text-sm">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          {progress}
        </div>
      )}
      <button
        onClick={handleExcel}
        disabled={isLoading}
        className="w-full flex items-center gap-4 p-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl hover:border-green-500/50 hover:bg-[var(--color-surface-hover)] transition-all text-left group disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
          {excelLoading ? (
            <div className="animate-spin w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full" />
          ) : (
            <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[var(--color-text)] group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
            {`Download ${t.course.title} Excel`}
          </div>
          <div className="text-sm text-[var(--color-text-muted)] mt-0.5">
            {`.xlsx with ${t.course.lower} summary, student overview, per-${t.lesson.lower} sheets, and activity details`}
          </div>
        </div>
        <Download className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors shrink-0" />
      </button>
      <button
        onClick={handlePdf}
        disabled={isLoading}
        className="w-full flex items-center gap-4 p-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl hover:border-red-500/30 hover:bg-[var(--color-surface-hover)] transition-all text-left group disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
          {pdfLoading ? (
            <div className="animate-spin w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full" />
          ) : (
            <FileText className="w-6 h-6 text-red-600 dark:text-red-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[var(--color-text)] group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
            {`Download ${t.course.title} PDF`}
          </div>
          <div className="text-sm text-[var(--color-text-muted)] mt-0.5">
            {`Printable report with summary, student overview, and per-${t.lesson.lower} performance tables`}
          </div>
        </div>
        <Download className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors shrink-0" />
      </button>
    </motion.div>
  )
}
